// KDS_biblioteca.js - Biblioteca de conocimiento industrial CON RECETAS
import React, { useState, useEffect, useMemo, useCallback, memo } from "react";
import PropTypes from "prop-types";
import {
  BookOpen,
  Search,
  X,
  FileText,
  AlertCircle,
  ChevronRight,
  ChevronLeft,
  Clock,
  CheckSquare,
  Trash2,
  Coffee,
  UtensilsCrossed,
  ClipboardList,
  FileCheck,
  Calendar,
  Shield,
  ArrowLeft,
  ChefHat,
  Users,
  RefreshCw
} from "lucide-react";
import { IP } from '../configuracion.js';

// ============================================
// CONSTANTES
// ============================================
const API_BASE = `${IP}/tavpos/backend`;

// ============================================
// DECODIFICADOR UNICODE
// ============================================
const decodeUnicode = (str) => {
  if (!str) return "";
  try {
    return str.replace(/\\u[\dA-Fa-f]{4}/g, (match) =>
      String.fromCharCode(parseInt(match.replace(/\\u/g, ""), 16))
    );
  } catch {
    return str;
  }
};

// ============================================
// PARSER DE CONOCIMIENTO
// ============================================
const parseKnowledgeContent = (text) => {
  if (!text) return [];
  
  const lines = text.split('\n');
  const sections = [];
  let currentSection = null;
  let currentSubsection = null;
  let buffer = [];
  
  const flushBuffer = () => {
    if (buffer.length > 0 && currentSection) {
      const textContent = buffer.join(' ').trim();
      if (textContent) {
        if (currentSubsection) {
          if (!currentSubsection.items) currentSubsection.items = [];
          currentSubsection.items.push({ type: 'text', text: textContent });
        } else if (currentSection) {
          if (!currentSection.items) currentSection.items = [];
          currentSection.items.push({ type: 'text', text: textContent });
        }
      }
      buffer = [];
    }
  };
  
  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];
    const trimmed = line.trim();
    
    if (!trimmed) {
      flushBuffer();
      continue;
    }
    
    if (trimmed.includes('===') && trimmed.length > 10) {
      flushBuffer();
      const nextLine = lines[i + 1]?.trim();
      if (nextLine && !nextLine.includes('===')) {
        if (currentSection) sections.push(currentSection);
        currentSection = {
          id: `section-${sections.length}`,
          title: decodeUnicode(nextLine),
          type: "main",
          subsections: [],
          items: []
        };
        i++;
      }
      continue;
    }
    
    if (trimmed.match(/^={10,}$/)) {
      flushBuffer();
      const nextLine = lines[i + 1]?.trim();
      if (nextLine && !nextLine.includes('===') && nextLine.length > 0) {
        if (currentSection) sections.push(currentSection);
        currentSection = {
          id: `section-${sections.length}`,
          title: decodeUnicode(nextLine),
          type: "main",
          subsections: [],
          items: []
        };
        i++;
      }
      continue;
    }
    
    if (trimmed.match(/^-{10,}$/)) {
      flushBuffer();
      const prevLine = lines[i - 1]?.trim();
      if (prevLine && currentSection) {
        currentSubsection = {
          id: `sub-${currentSection.subsections.length}`,
          title: decodeUnicode(prevLine),
          type: "subsection",
          items: []
        };
        currentSection.subsections.push(currentSubsection);
      }
      continue;
    }
    
    if (trimmed.startsWith('[ ]') || trimmed.startsWith('[_]') || trimmed.startsWith('[*]') || trimmed.match(/^\[\s*\]/)) {
      flushBuffer();
      const itemText = trimmed.replace(/^\[[\s_*]\]\s*/, '').trim();
      if (itemText) {
        if (currentSubsection) {
          currentSubsection.items.push({ type: 'checklist', text: decodeUnicode(itemText) });
        } else if (currentSection) {
          currentSection.items.push({ type: 'checklist', text: decodeUnicode(itemText) });
        }
      }
      continue;
    }
    
    if (trimmed.includes('_____')) {
      flushBuffer();
      const fieldName = trimmed.replace(/_____/g, '___________');
      if (currentSubsection) {
        currentSubsection.items.push({ type: 'field', text: decodeUnicode(fieldName) });
      } else if (currentSection) {
        currentSection.items.push({ type: 'field', text: decodeUnicode(fieldName) });
      }
      continue;
    }
    
    if (trimmed.startsWith('OBSERVACIONES:') || trimmed.startsWith('OBSERVACIONES:')) {
      flushBuffer();
      if (currentSubsection) {
        currentSubsection.items.push({ type: 'subtitle', text: '📋 OBSERVACIONES' });
      } else if (currentSection) {
        currentSection.items.push({ type: 'subtitle', text: '📋 OBSERVACIONES' });
      }
      continue;
    }
    
    buffer.push(decodeUnicode(trimmed));
  }
  
  flushBuffer();
  
  return sections.filter(s => s.title && s.title.length > 0 && s.title !== '===');
};

// ============================================
// PARSER DE RECETAS (IDÉNTICO AL DEL KDS.js)
// ============================================
const parseRecipeContent = (text, productId, productName) => {
  const lines = text.split('\n');
  const recipe = {
    id: productId,
    nombre: productName || '',
    descripcion: '',
    tiempo_preparacion: '',
    porciones: '',
    ingredientes: [],
    preparacion: [],
    tips: '',
    categoria: '',
    nivel: ''
  };
  
  let currentSection = '';
  
  for (const line of lines) {
    let trimmed = line.trim();
    if (!trimmed) continue;
    trimmed = decodeUnicode(trimmed);
    
    if (trimmed.startsWith('RECETA:')) recipe.nombre = trimmed.replace('RECETA:', '').trim();
    else if (trimmed.startsWith('DESCRIPCION:')) recipe.descripcion = trimmed.replace('DESCRIPCION:', '').trim();
    else if (trimmed.startsWith('TIEMPO_PREPARACION:')) recipe.tiempo_preparacion = trimmed.replace('TIEMPO_PREPARACION:', '').trim();
    else if (trimmed.startsWith('PORCIONES:')) recipe.porciones = trimmed.replace('PORCIONES:', '').trim();
    else if (trimmed === 'INGREDIENTES:') currentSection = 'ingredientes';
    else if (trimmed === 'PREPARACION:') currentSection = 'preparacion';
    else if (trimmed === 'TIPS:') currentSection = 'tips';
    else if (trimmed.startsWith('CATEGORIA:')) recipe.categoria = trimmed.replace('CATEGORIA:', '').trim();
    else if (trimmed.startsWith('NIVEL:')) recipe.nivel = trimmed.replace('NIVEL:', '').trim();
    else if (currentSection === 'ingredientes' && trimmed.startsWith('-')) {
      recipe.ingredientes.push(trimmed.replace('-', '').trim());
    } else if (currentSection === 'preparacion' && /^\d+\./.test(trimmed)) {
      recipe.preparacion.push(trimmed.replace(/^\d+\.\s*/, '').trim());
    } else if (currentSection === 'tips' && trimmed) {
      recipe.tips += (recipe.tips ? ' ' : '') + trimmed;
    }
  }
  
  return recipe;
};

// ============================================
// COMPONENTES
// ============================================

const SearchBar = memo(({ value, onChange, onClose, onBack, viewMode, setViewMode }) => {
  return (
    <div className="library-search-container">
      <button className="library-back-btn" onClick={onBack} title="Volver al KDS">
        <ArrowLeft size={20} />
        <span>Volver</span>
      </button>
      
      <div className="library-view-toggle">
        <button 
          className={`view-toggle-btn ${viewMode === 'manual' ? 'active' : ''}`}
          onClick={() => setViewMode('manual')}
        >
          <BookOpen size={18} />
          <span>Manual</span>
        </button>
        <button 
          className={`view-toggle-btn ${viewMode === 'recetas' ? 'active' : ''}`}
          onClick={() => setViewMode('recetas')}
        >
          <ChefHat size={18} />
          <span>Recetas</span>
        </button>
      </div>
      
      <div className="library-search-input-wrapper">
        <Search size={20} className="search-icon" />
        <input
          type="text"
          className="library-search-input"
          placeholder={viewMode === 'manual' ? "Buscar en manual..." : "Buscar receta por nombre..."}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        {value && (
          <button className="search-clear" onClick={() => onChange('')}>
            <X size={16} />
          </button>
        )}
      </div>
      
      <button className="library-close-btn" onClick={onClose}>
        <X size={24} />
      </button>
    </div>
  );
});

SearchBar.displayName = 'SearchBar';

// Componente de tarjeta de sección del manual
const SectionCard = memo(({ section, searchTerm }) => {
  const [expanded, setExpanded] = useState(true);
  
  const getIconForTitle = (title) => {
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes('checklist') || lowerTitle.includes('apertura')) return <ClipboardList size={20} />;
    if (lowerTitle.includes('cierre')) return <Calendar size={20} />;
    if (lowerTitle.includes('política') || lowerTitle.includes('general')) return <Shield size={20} />;
    if (lowerTitle.includes('tiempo') || lowerTitle.includes('meta')) return <Clock size={20} />;
    if (lowerTitle.includes('limpieza')) return <CheckSquare size={20} />;
    if (lowerTitle.includes('cancelación')) return <Trash2 size={20} />;
    if (lowerTitle.includes('bar') || lowerTitle.includes('cafetería')) return <Coffee size={20} />;
    if (lowerTitle.includes('cocina')) return <UtensilsCrossed size={20} />;
    if (lowerTitle.includes('incidente') || lowerTitle.includes('observacion')) return <AlertCircle size={20} />;
    if (lowerTitle.includes('objetivo')) return <FileCheck size={20} />;
    return <FileText size={20} />;
  };
  
  const highlightText = (text) => {
    if (!searchTerm || searchTerm.length < 2) return text;
    try {
      const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      const parts = text.split(regex);
      return parts.map((part, i) => 
        regex.test(part) ? <mark key={i} className="search-highlight">{part}</mark> : part
      );
    } catch {
      return text;
    }
  };
  
  const renderItem = (item, idx) => {
    if (item.type === 'checklist') {
      return (
        <div key={idx} className="library-checklist-item">
          <div className="check-box"></div>
          <span className="checklist-text">{highlightText(item.text)}</span>
        </div>
      );
    }
    if (item.type === 'field') {
      return (
        <div key={idx} className="library-field-item">
          <span className="field-label">✍️</span>
          <span className="field-text">{highlightText(item.text)}</span>
        </div>
      );
    }
    if (item.type === 'subtitle') {
      return (
        <div key={idx} className="library-subtitle-item">
          <span className="subtitle-icon">⚠️</span>
          <span className="subtitle-text">{highlightText(item.text)}</span>
        </div>
      );
    }
    if (item.type === 'text' && item.text && item.text.length > 0) {
      if (item.text.includes('===') || item.text.includes('---') || item.text.length < 2) return null;
      return (
        <p key={idx} className="library-text-item">
          {highlightText(item.text)}
        </p>
      );
    }
    return null;
  };
  
  const containsSearchTerm = () => {
    if (!searchTerm || searchTerm.length < 2) return true;
    const searchLower = searchTerm.toLowerCase();
    if (section.title.toLowerCase().includes(searchLower)) return true;
    if (section.items?.some(item => item.text?.toLowerCase().includes(searchLower))) return true;
    if (section.subsections?.some(sub => 
      sub.title?.toLowerCase().includes(searchLower) ||
      sub.items?.some(item => item.text?.toLowerCase().includes(searchLower))
    )) return true;
    return false;
  };
  
  if (!containsSearchTerm()) return null;
  
  return (
    <div className="library-section-card">
      <div className="section-header" onClick={() => setExpanded(!expanded)}>
        <div className="section-header-left">
          {getIconForTitle(section.title)}
          <h3 className="section-title">{highlightText(section.title)}</h3>
        </div>
        <ChevronRight size={20} className={`section-chevron ${expanded ? 'expanded' : ''}`} />
      </div>
      
      {expanded && (
        <div className="section-content">
          {section.items && section.items.map((item, idx) => renderItem(item, idx))}
          {section.subsections && section.subsections.map((subsection, idx) => (
            <div key={idx} className="library-subsection">
              <div className="subsection-header">
                <h4>{highlightText(subsection.title)}</h4>
              </div>
              <div className="subsection-content">
                {subsection.items && subsection.items.map((item, itemIdx) => renderItem(item, itemIdx))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

SectionCard.displayName = 'SectionCard';

// Componente de tarjeta de receta
const RecipeCard = memo(({ recipe, searchTerm, onSelectRecipe }) => {
  const highlightText = (text) => {
    if (!searchTerm || searchTerm.length < 2) return text;
    try {
      const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      const parts = text.split(regex);
      return parts.map((part, i) => 
        regex.test(part) ? <mark key={i} className="search-highlight">{part}</mark> : part
      );
    } catch {
      return text;
    }
  };
  
  const containsSearchTerm = () => {
    if (!searchTerm || searchTerm.length < 2) return true;
    const searchLower = searchTerm.toLowerCase();
    return (recipe.nombre || '').toLowerCase().includes(searchLower) ||
           (recipe.descripcion || '').toLowerCase().includes(searchLower) ||
           (recipe.categoria || '').toLowerCase().includes(searchLower);
  };
  
  if (!containsSearchTerm()) return null;
  
  return (
    <div className="recipe-card" onClick={() => onSelectRecipe(recipe)}>
      <div className="recipe-card-header">
        <div className="recipe-icon">
          <ChefHat size={24} />
        </div>
        <div className="recipe-card-info">
          <h4>{highlightText(recipe.nombre)}</h4>
          <div className="recipe-meta">
            {recipe.categoria && <span className="recipe-category">{recipe.categoria}</span>}
            {recipe.tiempo_preparacion && (
              <span className="recipe-time">
                <Clock size={12} /> {recipe.tiempo_preparacion}
              </span>
            )}
            {recipe.nivel && <span className="recipe-level">🎓 {recipe.nivel}</span>}
          </div>
        </div>
        <ChevronRight size={18} className="recipe-arrow" />
      </div>
      {recipe.descripcion && (
        <p className="recipe-card-description">{highlightText(recipe.descripcion.substring(0, 100))}...</p>
      )}
    </div>
  );
});

RecipeCard.displayName = 'RecipeCard';

// Componente de detalle de receta
const RecipeDetail = memo(({ recipe, onBack }) => {
  return (
    <div className="recipe-detail-container">
      <div className="recipe-detail-header">
        <button className="recipe-detail-back" onClick={onBack}>
          <ChevronLeft size={20} />
          <span>Volver a recetas</span>
        </button>
        <div className="recipe-detail-title">
          <ChefHat size={32} />
          <div>
            <h2>{recipe.nombre}</h2>
            <div className="recipe-detail-meta">
              {recipe.categoria && <span>📁 {recipe.categoria}</span>}
              {recipe.tiempo_preparacion && <span>⏱️ {recipe.tiempo_preparacion}</span>}
              {recipe.porciones && <span>👥 {recipe.porciones}</span>}
              {recipe.nivel && <span>🎓 {recipe.nivel}</span>}
            </div>
          </div>
        </div>
      </div>
      
      <div className="recipe-detail-content">
        {recipe.descripcion && (
          <div className="recipe-detail-section">
            <h4>📝 Descripción</h4>
            <p>{recipe.descripcion}</p>
          </div>
        )}
        
        {recipe.ingredientes.length > 0 && (
          <div className="recipe-detail-section">
            <h4>🛒 Ingredientes</h4>
            <ul className="recipe-ingredients-list">
              {recipe.ingredientes.map((ing, idx) => (
                <li key={idx}>{ing}</li>
              ))}
            </ul>
          </div>
        )}
        
        {recipe.preparacion.length > 0 && (
          <div className="recipe-detail-section">
            <h4>👨‍🍳 Preparación</h4>
            <ol className="recipe-preparation-list">
              {recipe.preparacion.map((step, idx) => (
                <li key={idx}>{step}</li>
              ))}
            </ol>
          </div>
        )}
        
        {recipe.tips && (
          <div className="recipe-detail-section tips-section">
            <h4>💡 Tips del Chef</h4>
            <p>{recipe.tips}</p>
          </div>
        )}
      </div>
    </div>
  );
});

RecipeDetail.displayName = 'RecipeDetail';

// Estados de carga
const LoadingState = memo(() => (
  <div className="library-loading">
    <div className="library-spinner"></div>
    <p>Cargando contenido...</p>
  </div>
));

const ErrorState = memo(({ error, onRetry }) => (
  <div className="library-error">
    <AlertCircle size={48} />
    <h3>No se pudo cargar</h3>
    <p>{error}</p>
    <button onClick={onRetry} className="library-retry-btn">Reintentar</button>
  </div>
));

const EmptyState = memo(({ type }) => (
  <div className="library-empty">
    {type === 'manual' ? <BookOpen size={64} /> : <ChefHat size={64} />}
    <h3>{type === 'manual' ? 'No hay documentación' : 'No hay recetas disponibles'}</h3>
    <p>{type === 'manual' ? 'El archivo Conocimiento_KDS.txt no existe' : 'No se encontraron archivos de recetas en el sistema'}</p>
    <small>Contacta al administrador para agregar contenido</small>
  </div>
));

// ============================================
// COMPONENTE PRINCIPAL
// ============================================

const KDSBiblioteca = ({ isOpen, onClose, property }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sections, setSections] = useState([]);
  const [recetas, setRecetas] = useState([]);
  const [viewMode, setViewMode] = useState("manual");
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  
  // Cargar manual operativo
  const loadKnowledge = useCallback(async () => {
    if (!isOpen || viewMode !== 'manual') return;
    
    setLoading(true);
    setError(null);
    
    try {
      const baseUrl = `${API_BASE}/data/${property}/recetas/`;
      const knowledgeUrl = `${baseUrl}Conocimiento_KDS.txt`;
      const response = await fetch(knowledgeUrl);
      
      if (!response.ok) throw new Error(`No se encontró Conocimiento_KDS.txt`);
      
      const textContent = await response.text();
      const parsedSections = parseKnowledgeContent(textContent);
      setSections(parsedSections);
    } catch (err) {
      setError(err.message);
      setSections([]);
    } finally {
      setLoading(false);
    }
  }, [isOpen, property, viewMode]);
  
  // Cargar todas las recetas desde listar_recetas.php (en la MISMA carpeta que las recetas)
  const loadRecetas = useCallback(async () => {
    if (!isOpen || viewMode !== 'recetas') return;
    
    setLoading(true);
    setError(null);
    
    try {
      // listar_recetas.php está en la MISMA carpeta que los archivos .txt
      const baseUrl = `${API_BASE}/data/${property}/recetas/`;
      const listUrl = `${baseUrl}listar_recetas.php?property=${encodeURIComponent(property)}`;
      
      const listResponse = await fetch(listUrl);
      const listData = await listResponse.json();
      
      if (!listData.success) {
        throw new Error(listData.error || 'Error al listar recetas');
      }
      
      const recetasInfo = listData.data || [];
      
      if (recetasInfo.length === 0) {
        setRecetas([]);
        setLoading(false);
        return;
      }
      
      // Cargar cada receta completa
      const recetasCompletas = [];
      
      for (const info of recetasInfo) {
        try {
          const txtResponse = await fetch(`${baseUrl}${info.id}.txt`);
          if (txtResponse.ok) {
            const content = await txtResponse.text();
            const recipe = parseRecipeContent(content, info.id, info.nombre);
            recetasCompletas.push(recipe);
          }
        } catch (err) {
          console.error(`Error cargando receta ${info.id}:`, err);
        }
      }
      
      // Ordenar alfabéticamente por nombre
      recetasCompletas.sort((a, b) => {
        return (a.nombre || a.id).localeCompare(b.nombre || b.id);
      });
      
      setRecetas(recetasCompletas);
      
    } catch (err) {
      console.error("Error loading recetas:", err);
      setError(err.message);
      setRecetas([]);
    } finally {
      setLoading(false);
    }
  }, [isOpen, property, viewMode]);
  
  // Cargar según el modo
  useEffect(() => {
    if (isOpen) {
      if (viewMode === 'manual') {
        loadKnowledge();
      } else {
        loadRecetas();
      }
      setSelectedRecipe(null);
    }
  }, [isOpen, viewMode, loadKnowledge, loadRecetas]);
  
  // Filtrar recetas
  const filteredRecetas = useMemo(() => {
    if (!searchTerm || searchTerm.length < 2) return recetas;
    const searchLower = searchTerm.toLowerCase();
    return recetas.filter(r => 
      (r.nombre || '').toLowerCase().includes(searchLower) ||
      (r.descripcion || '').toLowerCase().includes(searchLower) ||
      (r.categoria || '').toLowerCase().includes(searchLower)
    );
  }, [recetas, searchTerm]);
  
  // Filtrar secciones
  const filteredSections = useMemo(() => {
    if (!searchTerm || searchTerm.length < 2) return sections;
    const searchLower = searchTerm.toLowerCase();
    return sections.filter(section => {
      if ((section.title || '').toLowerCase().includes(searchLower)) return true;
      if (section.items?.some(item => (item.text || '').toLowerCase().includes(searchLower))) return true;
      if (section.subsections?.some(sub => 
        (sub.title || '').toLowerCase().includes(searchLower) ||
        sub.items?.some(item => (item.text || '').toLowerCase().includes(searchLower))
      )) return true;
      return false;
    });
  }, [sections, searchTerm]);
  
  if (!isOpen) return null;
  
  // Vista de detalle de receta
  if (selectedRecipe) {
    return (
      <div className="kds-library-overlay" onClick={onClose}>
        <div className="kds-library-panel" onClick={(e) => e.stopPropagation()}>
          <RecipeDetail 
            recipe={selectedRecipe} 
            onBack={() => setSelectedRecipe(null)} 
          />
        </div>
      </div>
    );
  }
  
  return (
    <div className="kds-library-overlay" onClick={onClose}>
      <div className="kds-library-panel" onClick={(e) => e.stopPropagation()}>
        <div className="library-header">
          <div className="library-title-area">
            <BookOpen size={28} />
            <div>
              <h2>Centro de Conocimiento</h2>
              <p>Manual Operativo • Políticas • Recetas • Capacitación</p>
            </div>
          </div>
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            onClose={onClose}
            onBack={onClose}
            viewMode={viewMode}
            setViewMode={setViewMode}
          />
        </div>
        
        <div className="library-main-content">
          {loading ? (
            <LoadingState />
          ) : error ? (
            <ErrorState error={error} onRetry={() => viewMode === 'manual' ? loadKnowledge() : loadRecetas()} />
          ) : viewMode === 'manual' ? (
            sections.length === 0 ? (
              <EmptyState type="manual" />
            ) : (
              <div className="library-sections-container">
                {filteredSections.length === 0 && searchTerm ? (
                  <div className="no-search-results">
                    <Search size={32} />
                    <p>No se encontraron resultados para "{searchTerm}"</p>
                    <button onClick={() => setSearchTerm("")} className="clear-search-btn">Limpiar búsqueda</button>
                  </div>
                ) : (
                  filteredSections.map((section) => (
                    <SectionCard key={section.id} section={section} searchTerm={searchTerm} />
                  ))
                )}
              </div>
            )
          ) : (
            <div className="recetas-container">
              <div className="recetas-header">
                <ChefHat size={24} />
                <h3>Biblioteca de Recetas</h3>
                <span className="recetas-count">{filteredRecetas.length} recetas disponibles</span>
              </div>
              
              {filteredRecetas.length === 0 ? (
                <div className="no-search-results">
                  {searchTerm ? (
                    <>
                      <Search size={32} />
                      <p>No se encontraron recetas para "{searchTerm}"</p>
                      <button onClick={() => setSearchTerm("")} className="clear-search-btn">Limpiar búsqueda</button>
                    </>
                  ) : (
                    <EmptyState type="recetas" />
                  )}
                </div>
              ) : (
                <div className="recetas-grid">
                  {filteredRecetas.map((recipe, idx) => (
                    <RecipeCard 
                      key={idx} 
                      recipe={recipe} 
                      searchTerm={searchTerm}
                      onSelectRecipe={setSelectedRecipe}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="library-footer">
          <div className="footer-info">
            <span>📚 Manual Operativo tavpos</span>
            <span>🔧 Versión Industrial</span>
            {viewMode === 'recetas' && <span>🍳 {filteredRecetas.length} recetas</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

KDSBiblioteca.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  property: PropTypes.string
};

export default KDSBiblioteca;